import 'regenerator-runtime/runtime';
import { call, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  CheckoutProdukt,
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
  GlobalPut,
  ProdukttabelleEnum,
  UpdateBeginnAblaufPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  THH_HUND_UEBERSICHT_TITLE,
  THH_PFERD_UEBERSICHT_TITLE,
  ThhHundVorbelegungdaten,
  ThhPferdVorbelegungdaten,
  TkvVorbelegungdaten,
  TkvV2Vorbelegungdaten,
  ValueLabelElement,
  VhvV2Vorbelegungdaten,
  VwgV2Vorbelegungdaten,
  UNFALL_TITLE,
  UnfallVorbelegungdaten,
  Vorbelegungdaten,
  Vorvertrag,
} from '@ps20-frontend/frontend-models';
import { Tierart } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { convertDateFormatFromShortIsoToLocalState } from '@ps20-frontend/utils';
import { DashboardAction } from '../dashboard.actions';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { IAppState } from '../appState';
import {
  getTarifierteAngebote,
  selectRequestVorbelegung,
  selectWarenkorbAngebote,
} from '../selectors/warenkorb.selector';

const globalActions = new GlobalActions();

export function* beibehaltungSaga() {
  yield takeEvery(
    GlobalActions.VORVERTRAG_BEIBEHALTEN,
    safeSaga(vorvertragBeibehalten, App.SHOP_DASHBOARD),
  );
  yield takeLatest(GlobalActions.PRODUKT_ACTION, safeSaga(detectBeibehalten, App.SHOP_DASHBOARD));
  yield takeLatest(GlobalActions.PRODUKT_DELETED, safeSaga(detectBeibehalten, App.SHOP_DASHBOARD));
  yield takeLatest(
    GlobalActions.SAVED_STATES_LOADED,
    safeSaga(detectBeibehaltenAfterLoad, App.SHOP_DASHBOARD),
  );
}

function* triggerTarifierung(globalPut: GlobalPut, vorvertrag: Vorvertrag, state: IAppState) {
  const produkt = state.warenkorb.find(produkt =>
    produkt.vorvertragsNummern?.some(
      vorvertragnummer =>
        vorvertrag.vertragsnummer === vorvertragnummer.vertragsnummer &&
        vorvertrag.spLaufnummer === vorvertragnummer.spLaufnummer,
    ),
  );

  yield call(
    [globalPut, globalPut.put],
    globalActions.triggerTarifierungBeibehaltung({
      appId: produkt.appId,
      angebotsId: produkt.angebotsId,
    }),
  );
  const { success, failed } = yield race({
    success: take(GlobalActions.TARIFIERE_SHOP_SUCCESS),
    failed: take(GlobalActions.TARIFIERE_SHOP_FAILED),
  });
  if (success) {
    const newState: IAppState = yield select();
    const checkoutProduktPayload = mapCheckoutProdukt(vorvertrag, newState);
    if (checkoutProduktPayload) {
      yield call(globalPut.put, globalActions.produktAction(checkoutProduktPayload));
    }
  }
  if (failed) {
    const produkt = state.warenkorb.find(produkt =>
      produkt.vorvertragsNummern?.some(
        vorvertragnummer =>
          vorvertrag.vertragsnummer === vorvertragnummer.vertragsnummer &&
          vorvertrag.spLaufnummer === vorvertragnummer.spLaufnummer,
      ),
    );
    const produktDeletedAction = globalActions.produktDeleted(
      [
        {
          appId: produkt.appId,
          angebotsId: produkt.angebotsId,
        },
      ],
      produkt.vorvertragsNummern,
    );
    yield call(GlobalPut.getInstance().put, produktDeletedAction);
  }
}

export function* vorvertragBeibehalten(action: DashboardAction<Vorvertrag>) {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const vorvertrag: Vorvertrag = action.payload;

  requestVorbelegung(vorvertrag, state);
  yield take(GlobalActions.VORBELEGUNG_FINISHED);
  yield* triggerTarifierung(globalPut, vorvertrag, state);
}

export function* detectBeibehaltenAfterLoad() {
  const warenkorbAngebote: WarenkorbAngebot[] = yield select(selectWarenkorbAngebote);
  const beibehaltenesProdukt: WarenkorbAngebot = warenkorbAngebote.find(
    angebot => angebot.beibehalten,
  );
  yield call(
    GlobalPut.getInstance().put,
    globalActions.provideIsBeibehaltenesAngebotInWarenkorb(beibehaltenesProdukt !== undefined),
  );
}

export function* detectBeibehalten(action: GlobalAction<CheckoutProdukt>) {
  const produkteInWarenkorb: Array<WarenkorbAngebot> = yield select(getTarifierteAngebote);
  const beibehaltenesProdukt: WarenkorbAngebot = produkteInWarenkorb.find(
    value => value.beibehalten,
  );

  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put,
    globalActions.provideIsBeibehaltenesAngebotInWarenkorb(beibehaltenesProdukt !== undefined),
  );

  if (action.payload.beibehalten) {
    yield call(
      globalPut.put,
      globalActions.resetBeginnAblauf(beibehaltenesProdukt.appId, beibehaltenesProdukt.angebotsId),
    );
    const updateBeginnAblaufPayload: UpdateBeginnAblaufPayload = {
      appId: beibehaltenesProdukt.appId,
      angebotsId: beibehaltenesProdukt.angebotsId,
    };
    const wirksamkeitsbeginn = beibehaltenesProdukt.wirksamkeitsbeginn;
    if (wirksamkeitsbeginn) {
      updateBeginnAblaufPayload.beginn = wirksamkeitsbeginn;
    }
    yield call(globalPut.put, globalActions.updateBeginnAblauf(updateBeginnAblaufPayload));
  }
}

function determineProdukttabelle(vorbelegungdaten: Vorbelegungdaten): ProdukttabelleEnum {
  let produkttabelle: ProdukttabelleEnum = null;
  if (vorbelegungdaten?.forApp === App.TKV) {
    produkttabelle =
      (vorbelegungdaten as TkvVorbelegungdaten).tierart === Tierart.VRPFERD
        ? ProdukttabelleEnum.TKV_PFERD
        : ProdukttabelleEnum.TKV_HUND_KATZE;
  } else if (vorbelegungdaten?.forApp === App.RS) {
    if (vorbelegungdaten.verkaufsangebotKurzname === 'RS005') {
      produkttabelle = ProdukttabelleEnum.RS_VERKEHR;
    }
  }
  // Note: TKV_V2 is handled separately in requestVorbelegung by dispatching BEIBEHALTUNG_PRODUKT_REQUESTED
  return produkttabelle;
}

function requestVorbelegung(vorvertrag: Vorvertrag, state: IAppState) {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();

  const selectedRequestVorbelegung = selectRequestVorbelegung(vorvertrag, state);

  if (selectedRequestVorbelegung.produkt.appId === App.TKV_V2) {
    const tkvVorbelegungdaten = selectedRequestVorbelegung.vorbelegungdaten as TkvV2Vorbelegungdaten;
    globalEventDispatcher.dispatch(
      globalActions.beibehaltungProduktRequested({
        angebotsId: selectedRequestVorbelegung.produkt.angebotsId,
        vorbelegungdaten: tkvVorbelegungdaten,
      }),
    );
  } else if (selectedRequestVorbelegung.produkt.appId === App.VWG_V2) {
    const vwgVorbelegungdaten = selectedRequestVorbelegung.vorbelegungdaten as VwgV2Vorbelegungdaten;
    globalEventDispatcher.dispatch(
      globalActions.beibehaltungProduktRequested({
        angebotsId: selectedRequestVorbelegung.produkt.angebotsId,
        vorbelegungdaten: vwgVorbelegungdaten,
      }),
    );
  } else if (selectedRequestVorbelegung.produkt.appId === App.VHV_V2) {
    const vhvVorbelegungdaten = selectedRequestVorbelegung.vorbelegungdaten as VhvV2Vorbelegungdaten;
    globalEventDispatcher.dispatch(
      globalActions.beibehaltungProduktRequested({
        angebotsId: selectedRequestVorbelegung.produkt.angebotsId,
        vorbelegungdaten: vhvVorbelegungdaten,
      }),
    );
  } else {
    const produkttabelle = determineProdukttabelle(selectedRequestVorbelegung.vorbelegungdaten);
    globalEventDispatcher.dispatch(
      globalActions.produktRequested({
        appId: selectedRequestVorbelegung.produkt.appId,
        produkttabelle,
        angebotsId: selectedRequestVorbelegung.produkt.angebotsId,
      }),
    );
  }

  const fremdVnChanged = state.transientState?.fremdVnChanged;
  const isVnWechsel = fremdVnChanged?.addVertraege?.some(
    v => v.vorvertragsnummer?.vertragsnummer === vorvertrag.vertragsnummer
  ) ?? false;

  if (selectedRequestVorbelegung.vorbelegungPayload.vorhandeneVorbelegungdaten) {
    selectedRequestVorbelegung.vorbelegungPayload.vorhandeneVorbelegungdaten.aenderungdatum =
      vorvertrag.aenderungdatum;
    selectedRequestVorbelegung.vorbelegungPayload.vorhandeneVorbelegungdaten.isVnWechsel =
      isVnWechsel;
  }
  const vorbelegungPayload = {
    ...selectedRequestVorbelegung.vorbelegungPayload,
    isVnWechsel,
  };
  globalEventDispatcher.dispatch(
    globalActions.vorbelegungRequested(vorbelegungPayload),
  );
}

function mapCheckoutProdukt(vorvertrag: Vorvertrag, dashboardState: IAppState) {
  const warenkorbAngebot: WarenkorbAngebot = dashboardState.warenkorb
    .filter(angebot => angebot?.vorvertragsNummern)
    .find(produkt =>
      produkt.vorvertragsNummern.some(
        nummern =>
          nummern.vertragsnummer === vorvertrag.vertragsnummer &&
          nummern.spLaufnummer === vorvertrag.spLaufnummer,
      ),
    );

  if (warenkorbAngebot) {
    const checkoutProdukt: CheckoutProdukt = {
      appId: warenkorbAngebot.appId,
      angebotsId: warenkorbAngebot.angebotsId,
      uebersichtTitle: warenkorbAngebot.uebersichtTitle,
      laufzeit: warenkorbAngebot.laufzeit,
      versichertesRisiko: warenkorbAngebot.versichertesRisiko,
      vorvertragsnummer: warenkorbAngebot.vorvertragsNummern,
      vorvertragSofortschutz: warenkorbAngebot.sofortschutzAvailable,
      wirksamkeitsbeginn: warenkorbAngebot.wirksamkeitsbeginn,
      shouldDisableBeginndatum: warenkorbAngebot.shouldDisableBeginndatum,
      beibehalten: true,
      beitragsfreiBeibehalten: warenkorbAngebot.beitragsfreiBeibehalten,
    };
    switch (warenkorbAngebot.appId) {
      case App.VHV: {
        return {
          showAngabenZumHaushalt: false,
          ...checkoutProdukt,
        };
      }
      case App.VHV_V2: {
        return {
          showAngabenZumHaushalt: false,
          ...checkoutProdukt,
        };
      }
      case App.PH: {
        return {
          anzahlVersicherterWohnungen: null,
          ...checkoutProdukt,
        } as CheckoutProdukt;
      }
      case App.THH_PFERD: {
        checkoutProdukt.uebersichtTitle = THH_PFERD_UEBERSICHT_TITLE;
        const vorbelegungDaten: ThhPferdVorbelegungdaten = vorvertrag.vorbelegungdaten.find(
          value => value.forApp === App.THH_PFERD,
        ) as ThhPferdVorbelegungdaten;
        return {
          anzahlPferde: null,
          tiergroesseList: vorbelegungDaten?.stockmassListe,
          ...checkoutProdukt,
        } as CheckoutProdukt;
      }
      case App.THH_HUND: {
        checkoutProdukt.uebersichtTitle = THH_HUND_UEBERSICHT_TITLE;
        const vorbelegungDaten: ThhHundVorbelegungdaten = vorvertrag.vorbelegungdaten.find(
          value => value.forApp === App.THH_HUND,
        ) as ThhHundVorbelegungdaten;
        return {
          ausgewaehlteHunderassen: vorbelegungDaten?.tierrasse,
          ausgewaehlteHunderassenValue: vorbelegungDaten?.tierrasse,
          ...checkoutProdukt,
        };
      }
      case App.UNF: {
        checkoutProdukt.uebersichtTitle = UNFALL_TITLE;
        const vorbelegungDaten: UnfallVorbelegungdaten = vorvertrag.vorbelegungdaten.find(
          value => value.forApp === App.UNF,
        ) as UnfallVorbelegungdaten;
        return {
          ...checkoutProdukt,
          produktangebotsId: vorbelegungDaten.angebotsId, // Wahrscheinlich sollte das schon im CheckoutProdukt verpflichted gesetzt werden. Ist aber momentan optional
          unfallVp: {
            vorname: vorbelegungDaten.vorname,
            nachname: vorbelegungDaten.nachname,
            geburtsdatum: vorbelegungDaten.geburtsdatum,
          },
        };
      }
      case App.TKV: {
        const dataForBeibehaltungTkv: TkvVorbelegungdaten = vorvertrag.vorbelegungdaten?.find(
          ({ forApp }) => forApp === App.TKV,
        ) as TkvVorbelegungdaten;

        return {
          tierart: dataForBeibehaltungTkv?.tierart,
          inBehandlung: dataForBeibehaltungTkv?.hasHeilbehandlungen,
          rasse: {
            value: dataForBeibehaltungTkv?.tierrasse,
            label: null,
          } as ValueLabelElement,
          geburtsdatumTier: convertDateFormatFromShortIsoToLocalState(
            dataForBeibehaltungTkv?.geburtsdatumTier,
          ),
          geschlecht: dataForBeibehaltungTkv?.tiergeschlecht,
          tierhaltung: dataForBeibehaltungTkv?.tierhaltung,
          isTierKastriert: dataForBeibehaltungTkv?.isKastriert,
          ...checkoutProdukt,
        };
      }
      case App.TKV_V2: {
        const dataForBeibehaltungTkv: TkvV2Vorbelegungdaten = vorvertrag.vorbelegungdaten?.find(
          ({ forApp }) => forApp === App.TKV_V2,
        ) as TkvV2Vorbelegungdaten;

        return {
          animalType: dataForBeibehaltungTkv?.tierart,
          breed: {
            value: dataForBeibehaltungTkv?.tierrasse,
            label: null,
          } as ValueLabelElement,
          animalBirthDate: convertDateFormatFromShortIsoToLocalState(
            dataForBeibehaltungTkv?.geburtsdatumTier,
          ),
          animalName: dataForBeibehaltungTkv?.tiername,
          animalGender: dataForBeibehaltungTkv?.tiergeschlecht,
          catKeepingType: dataForBeibehaltungTkv?.tierhaltung,
          animalIdentificationType: dataForBeibehaltungTkv?.kennzeichnungsArt,
          animalIdentification: dataForBeibehaltungTkv?.kennzeichen,
          isAssistanceDog: dataForBeibehaltungTkv?.hasAssistenzhund,
          ...checkoutProdukt,
        };
      }
      case App.RS: {
        return {
          hasEinFahrzeugVersichert: null,
          isKombischutzSelected: null,
          ...checkoutProdukt,
        } as CheckoutProdukt;
      }
      case App.VWG: {
        return {
          ...checkoutProdukt,
        };
      }
      case App.VWG_V2: {
        const vorbelegungDaten: VwgV2Vorbelegungdaten = vorvertrag.vorbelegungdaten?.find(
          ({ forApp }) => forApp === App.VWG_V2,
        ) as VwgV2Vorbelegungdaten;
        return {
          ...checkoutProdukt,
          nebengebaeude: vorbelegungDaten?.nebengebaeude,
          baujahr: vorbelegungDaten?.baujahr,
          merkmale: vorbelegungDaten?.merkmale,
        };
      }

      default:
        return checkoutProdukt;
    }
  }
}
